import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone } from "lucide-react";
import { CONTACT } from "../lib/site";

const PRESET = encodeURIComponent(
  "Hi AB Events, I'm interested in booking your band for my event. Could you share availability and pricing?"
);

export const WhatsAppFab = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  // Show after the intro loader is done (~3s)
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 3200);
    return () => clearTimeout(t);
  }, []);

  const phone = CONTACT.whatsapp.replace(/[^\d+]/g, "");
  const waLink = `https://wa.me/${phone.replace(/^\+/, "")}?text=${PRESET}`;
  const telLink = `tel:${phone}`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 right-6 z-[150] flex flex-col items-end gap-3"
          data-testid="whatsapp-fab"
        >
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                transition={{ duration: 0.3 }}
                className="bg-[#0d0d0d] border border-[#d4af37]/40 shadow-2xl shadow-black/60 backdrop-blur-xl w-[300px] sm:w-[340px] overflow-hidden"
              >
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]">
                      Talk to us
                    </div>
                    <div className="font-serif text-xl mt-1">AB Events Concierge</div>
                  </div>
                  <button
                    data-testid="whatsapp-fab-close"
                    onClick={() => setOpen(false)}
                    className="text-white/60 hover:text-white p-1"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="p-5">
                  <p className="text-sm text-white/65 leading-relaxed">
                    We typically reply in under 30 minutes. Send a quick message
                    and we'll share availability + tailored pricing.
                  </p>

                  <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="whatsapp-fab-chat"
                    className="mt-5 flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white px-5 py-3.5 rounded-sm font-medium tracking-wide text-sm transition-colors"
                  >
                    <MessageCircle size={18} />
                    Chat on WhatsApp
                  </a>

                  <a
                    href={telLink}
                    data-testid="whatsapp-fab-call"
                    className="mt-3 flex items-center gap-3 border border-white/15 hover:border-[#d4af37] hover:text-[#d4af37] text-white/80 px-5 py-3.5 rounded-sm font-medium tracking-wide text-sm transition-colors"
                  >
                    <Phone size={16} />
                    Call {CONTACT.phone}
                  </a>
                </div>

                <div className="border-t border-white/5 px-5 py-3 text-[10px] tracking-[0.25em] uppercase text-white/35 text-center">
                  ⌁ Bookings • India • Worldwide ⌁
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            data-testid="whatsapp-fab-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Open WhatsApp"
            className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(37,211,102,0.55)] transition-all duration-300 hover:scale-105"
          >
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
            {open ? <X size={22} /> : <MessageCircle size={22} className="relative" />}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppFab;
